import { ModeCheckBoxInputField, ModeSelectInputField, ModeTextInputField, ModeTextAreaInputField, SelectInputOption, useModeFormStyle }
    from '@moderepo/mode-app-components';
import { DeviceConfigParamSet, DeviceConfigParamType, DeviceConfigParamValueType } from '@moderepo/mode-internal-apis';
import { useMemo } from 'react';



export interface DeviceConfigParamSetCompProps {
    readonly deviceId: number;

    readonly isEditing?: boolean | undefined;

    // The parameter set schema
    readonly parameterSet: DeviceConfigParamSet;

    // The value to initialize the input fields with
    readonly inputValues: {
        [paramKey: string]: DeviceConfigParamValueType | undefined;
    } | undefined;

    readonly inputErrors?: {
        [paramKey: string]: string | undefined;
    } | undefined;

    readonly onInputChange: ((fieldName: string, value: any)=> void) | undefined;
    readonly onFieldClicked: ((fieldName: string)=> void) | undefined
}


/**
 * UI for device config parameters set.
 */
export const DeviceConfigParamSetComp: React.FC<DeviceConfigParamSetCompProps> = (props: DeviceConfigParamSetCompProps) => {

    const formClasses = useModeFormStyle();


    /**
     * Create and cache the array of options for each the param that has "options" configured.
     */
    const enumInputsOptionsByParamKey = useMemo((): {
        [paramKey: string]: readonly SelectInputOption<DeviceConfigParamValueType>[] | undefined;
    } => {
        const result = {};

        props.parameterSet.parameters.forEach((param) => {
            // Only params of type STRING or NUMBER can have enum options. The other types e.g. BOOLEAN, ARRAY, OBJECT doesn't make
            // sense to have options
            if (param.options) {
                result[param.key] = param.options.map((option): SelectInputOption<DeviceConfigParamValueType> => {
                    return {
                        label: option.label,
                        value: option.value,
                    };
                });
            }
        });

        return result;
    }, [props.parameterSet.parameters]);



    return (
        <>
            {props.parameterSet.parameters.map((param) => {
                switch (param.type) {
                    case DeviceConfigParamType.STRING:
                    case DeviceConfigParamType.NUMBER:
                    case DeviceConfigParamType.OBJECT:
                    case DeviceConfigParamType.ARRAY:
                        // If enum options are provided for this param, use the SELECT input control
                        if (enumInputsOptionsByParamKey[param.key]) {
                            return (
                                <ModeSelectInputField
                                    key={param.key}
                                    className={formClasses.formInputField}
                                    fieldName={param.key}
                                    value={props.inputValues?.[param.key]}
                                    options={enumInputsOptionsByParamKey[param.key]}
                                    isInEditMode={props.isEditing}
                                    onFormInputChange={props.onInputChange}
                                    onFieldClicked={props.onFieldClicked}
                                    editable
                                    label={param.label}
                                    required={param.required}
                                    variant="standard"
                                    error={props.inputErrors?.[param.key]}
                                    description={param.description}
                                />
                            );
                        }

                        if (param.type === DeviceConfigParamType.OBJECT || param.type === DeviceConfigParamType.ARRAY) {
                            return (
                                <ModeTextAreaInputField
                                    key={param.key}
                                    className={formClasses.formInputField}
                                    fieldName={param.key}
                                    value={props.inputValues?.[param.key]?.toString()}
                                    isInEditMode={props.isEditing}
                                    onInputChange={props.onInputChange}
                                    onFieldClicked={props.onFieldClicked}
                                    editable
                                    label={param.label}
                                    required={param.required}
                                    variant="standard"
                                    error={props.inputErrors?.[param.key]}
                                    description={param.description}
                                />
                            );
                        }

                        // Use TEXT input control
                        return (
                            <ModeTextInputField
                                key={param.key}
                                className={formClasses.formInputField}
                                fieldName={param.key}
                                inputType={param.type === DeviceConfigParamType.NUMBER ? 'number' : 'text'}
                                value={props.inputValues?.[param.key]}
                                isInEditMode={props.isEditing}
                                onInputChange={props.onInputChange}
                                onFieldClicked={props.onFieldClicked}
                                editable
                                label={param.label}
                                required={param.required}
                                variant="standard"
                                error={props.inputErrors?.[param.key]}
                                description={param.description}
                            />
                        );


                    case DeviceConfigParamType.BOOLEAN:
                        return (
                            <ModeCheckBoxInputField
                                key={param.key}
                                className={formClasses.formInputField}
                                fieldName={param.key}
                                value={Boolean(props.inputValues?.[param.key])}
                                isInEditMode={props.isEditing}
                                onInputChange={props.onInputChange}
                                onFieldClicked={props.onFieldClicked}
                                editable
                                label={param.label}
                                required={param.required}
                                variant="standard"
                                error={props.inputErrors?.[param.key]}
                                description={param.description}
                            />
                        );

                    default:
                        return (
                            <div key={param.key} style={{ display: 'hidden' }} />
                        );
                }
            })}
        </>
    );
};
